import { BookOpen, Download, ListTree, Moon, Sun, Type } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReaderBranch, ReaderExportFormat } from '@/api/readerApi'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { ReaderSettings } from './readerSettings'
import { formatCount } from '@/utils/format'

interface ReaderToolbarProps {
  title: string
  branch?: ReaderBranch
  settings: ReaderSettings
  exportingFormat: ReaderExportFormat | null
  onOpenToc: () => void
  onSettingsChange: (settings: ReaderSettings) => void
  onBranchChange: (branchId: string) => void
  onOpenExportCenter: () => void
}

export function ReaderToolbar({
  title,
  branch,
  settings,
  exportingFormat,
  onOpenToc,
  onSettingsChange,
  onBranchChange,
  onOpenExportCenter,
}: ReaderToolbarProps) {
  const { t } = useTranslation()

  return (
    <div className="sticky top-14 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex min-h-14 max-w-[1600px] flex-wrap items-center gap-2 px-3 py-2 sm:px-6">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={onOpenToc} aria-label={t('reader.toc')}>
              <ListTree className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('reader.toc')}</TooltipContent>
        </Tooltip>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
        </div>

        {branch?.available?.length ? (
          <Select value={branch.current} onValueChange={onBranchChange}>
            <SelectTrigger className="h-9 w-[118px] text-xs" aria-label={t('reader.branch')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {branch.available.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        <div className="hidden min-w-[170px] items-center gap-2 md:flex">
          <Type className="size-4 text-muted-foreground" />
          <Slider
            min={16}
            max={24}
            step={1}
            value={[settings.fontSize]}
            onValueChange={([value]) => {
              if (typeof value === 'number') onSettingsChange({ ...settings, fontSize: value })
            }}
            aria-label={t('reader.fontSizeLabel')}
          />
          <span className="w-9 text-xs text-muted-foreground">{formatCount(settings.fontSize)}</span>
        </div>

        <ToggleGroup
          type="single"
          value={settings.fontFamily}
          onValueChange={(value) => {
            if (value === 'serif' || value === 'kai') onSettingsChange({ ...settings, fontFamily: value })
          }}
          aria-label={t('reader.fontFamily')}
        >
          <ToggleGroupItem value="serif" className="h-7 px-2" aria-label={t('reader.fontSong')}>
            <BookOpen className="size-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="kai" className="h-7 px-2" aria-label={t('reader.fontKai')}>
            <Type className="size-4" />
          </ToggleGroupItem>
        </ToggleGroup>

        <ToggleGroup
          type="single"
          value={settings.theme}
          onValueChange={(value) => {
            if (value === 'paper' || value === 'night') onSettingsChange({ ...settings, theme: value })
          }}
          aria-label={t('reader.theme')}
        >
          <ToggleGroupItem value="paper" className="h-7 px-2" aria-label={t('reader.paperTheme')}>
            <Sun className="size-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="night" className="h-7 px-2" aria-label={t('reader.nightTheme')}>
            <Moon className="size-4" />
          </ToggleGroupItem>
        </ToggleGroup>

        <Button variant="outline" size="sm" disabled={Boolean(exportingFormat)} onClick={onOpenExportCenter}>
          <Download className={exportingFormat ? 'size-4 animate-pulse' : 'size-4'} />
          <span className="hidden sm:inline">{exportingFormat ? t('reader.exporting') : t('reader.export')}</span>
        </Button>
      </div>
    </div>
  )
}
